import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { callApi, CDN_URL } from "../Helpers";
import { getInlineLoader } from "../Helpers";
import {
  MdBadge,
  MdDescription,
  MdEdit,
  MdLocationOn,
  MdOpenInNew,
  MdPerson,
  MdPhone,
  MdPictureAsPdf,
  MdVerifiedUser,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
} from "react-icons/md";

export default function View({ uuid }) {
  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  
  // State to track which guarantor dropdown is currently expanded
  const [expandedGuarantorIdx, setExpandedGuarantorIdx] = useState(null);

  async function init() {
    setReady(false);
    await Promise.all([getData()]);
    setReady(true);
  }

  async function getData() {
    await callApi("get-customer-data", { uuid }).then((response) => {
      if (response.status === 1) {
        setData(response.data);
      } else {
        appContext.tellError("Could not fetch data");
      }
    });
  }

  const getDocumentUrl = (path) => {
    if (!path) return "";
    return path.startsWith("http") ? path : `${CDN_URL}${path}`;
  };

  const isImageFile = (url) => /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|$)/i.test(url);
  const isPdfFile = (url) => /\.pdf(\?|$)/i.test(url);

  const toggleGuarantor = (idx) => {
    setExpandedGuarantorIdx(expandedGuarantorIdx === idx ? null : idx);
  };

  const renderDetailField = (label, value, icon = null) => (
    <div className="col-md-6 mb-4">
      <label className="form-label text-muted small fw-bold text-uppercase">{label}</label>
      <h6 className="d-flex align-items-center gap-2 mb-0">
        {icon}
        <span>{value || "Not set"}</span>
      </h6>
    </div>
  );

  const renderDocumentPreview = (label, path, emptyText) => {
    const url = getDocumentUrl(path);

    return (
      <div className="col-md-6 mb-4 d-flex">
        <div className="detail-actions-card w-100 h-100 d-flex flex-column shadow-sm border-0">
          <label className="form-label text-muted small fw-bold text-uppercase">{label}</label>

          {url ? (
            <>
              <div
                className="mt-2 mb-3 overflow-hidden border rounded-3 bg-light position-relative"
                style={{ minHeight: "240px" }}
              >
                {isImageFile(url) ? (
                  <img
                    src={url}
                    alt={label}
                    className="img-fluid d-block w-100 h-100"
                    style={{ minHeight: "240px", objectFit: "cover" }}
                  />
                ) : isPdfFile(url) ? (
                  <iframe
                    src={url}
                    title={label}
                    className="w-100 border-0"
                    style={{ height: "240px" }}
                  />
                ) : (
                  <div
                    className="d-flex flex-column align-items-center justify-content-center text-center text-muted p-4 h-100"
                    style={{ minHeight: "240px" }}
                  >
                    <MdDescription size={28} />
                    <span className="small mt-2">Preview unavailable for this document type</span>
                  </div>
                )}
              </div>

              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="btn btn-outline-primary btn-sm w-100 d-flex align-items-center justify-content-center gap-2 mt-auto"
              >
                {isPdfFile(url) ? <MdPictureAsPdf size={16} /> : <MdOpenInNew size={16} />}
                Open full document
              </a>
            </>
          ) : (
            <div
              className="d-flex flex-column align-items-center justify-content-center text-center text-muted border rounded-3 bg-light p-4 mt-2"
              style={{ minHeight: "240px" }}
            >
              <MdVerifiedUser size={28} className="opacity-50" />
              <span className="small mt-2">{emptyText}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    init();
  }, [uuid]);

  useEffect(() => {
    appContext.setShowOverlayLoader(loading);
    return () => {
      appContext.setShowOverlayLoader(false);
    };
  }, [loading]);

  if (ready && data) {
    const customerFields = [
      {
        label: "Fullname",
        value: data.fullname,
        icon: <MdPerson size={16} className="text-primary" />,
      },
      {
        label: "Phone Number",
        value: data.phone,
        icon: <MdPhone size={16} className="text-primary" />,
      },
      {
        label: "Address",
        value: data.address,
        icon: <MdLocationOn size={16} className="text-primary" />,
      },
      {
        label: "National ID",
        value: data.nationalID,
        icon: <MdBadge size={16} className="text-primary" />,
      },
    ];

    const verificationFields = [
      {
        label: "TIN Number",
        value: data.tinNumber,
        icon: <MdBadge size={16} className="text-primary" />,
      },
      {
        label: "Driving License Number",
        value: data.drivingLicenseNumber,
        icon: <MdVerifiedUser size={16} className="text-primary" />,
      },
    ];

    // Consolidate legacy flat fields or new dynamic array into a single mapped list
    let guarantorsList = [];
    if (data.guarantors && Array.isArray(data.guarantors) && data.guarantors.length > 0) {
      guarantorsList = data.guarantors;
    } else if (data.guarantorFullname) {
      guarantorsList = [{
        fullname: data.guarantorFullname,
        phone: data.guarantorPhone,
        passportDocument: data.guarantorPassport
      }];
    }

    return (
      <div className="container">
        <div className="detail-shell">
          <div className="detail-header flex-column">
            <div>
              <p className="app-page-eyebrow">Customer profile</p>
              <h4 className="main-section-title mb-1">{data.fullname}</h4>
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap justify-content-md-end">
              <button
                className="btn btn-dark btn-rounded app-cta-btn"
                onClick={() => {
                  appContext.navTo({
                    item: "view",
                    subItem: "create-customer",
                    extraItem: data.uuid,
                  });
                }}
              >
                <MdEdit size={18} />
                Edit
              </button>
            </div>
          </div>

          <div className="detail-actions-card mt-4">
            <h6 className="card-title mb-4">Customer Information</h6>
            <div className="row">
              {customerFields.map((field) => renderDetailField(field.label, field.value, field.icon))}
            </div>
          </div>

          <div className="detail-actions-card mt-4">
            <h6 className="card-title mb-4">Verification Details</h6>
            <div className="row">
              {verificationFields.map((field) => renderDetailField(field.label, field.value, field.icon))}
            </div>
          </div>

          {/* DYNAMIC GUARANTORS LIST ACCORDION */}
          <div className="detail-actions-card mt-4">
            <h6 className="card-title mb-4">Guarantors Overview</h6>
            
            {guarantorsList.length > 0 ? (
              guarantorsList.map((g, idx) => (
                <div key={idx} className="border rounded-3 mb-3 bg-white shadow-sm overflow-hidden">
                  
                  {/* Dropdown Header / Trigger */}
                  <div
                    className="p-3 d-flex justify-content-between align-items-center user-select-none"
                    onClick={() => toggleGuarantor(idx)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <div className="bg-light text-primary rounded-circle d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                        <MdPerson size={20} />
                      </div>
                      <div>
                        <h6 className="mb-0 fw-bold">{g.fullname || g.guarantorFullname || `Unnamed Guarantor ${idx + 1}`}</h6>
                        <span className="text-muted small">{g.phone || g.guarantorPhone || "No phone provided"}</span>
                      </div>
                    </div>
                    <div className="text-muted">
                      {expandedGuarantorIdx === idx ? <MdKeyboardArrowUp size={24} /> : <MdKeyboardArrowDown size={24} />}
                    </div>
                  </div>

                  {/* Dropdown Expanded Body */}
                  {expandedGuarantorIdx === idx && (
                    <div className="p-4 border-top bg-light">
                      <div className="row">
                        {renderDetailField("Guarantor Fullname", g.fullname || g.guarantorFullname, <MdPerson size={16} className="text-primary" />)}
                        {renderDetailField("Guarantor Phone", g.phone || g.guarantorPhone, <MdPhone size={16} className="text-primary" />)}
                      </div>
                      
                      <div className="row mt-2">
                        {renderDocumentPreview(
                          "Identity Document", 
                          g.passportDocument || g.guarantorPassport || g.passport, 
                          "Guarantor document has not been uploaded"
                        )}
                      </div>
                    </div>
                  )}
                  
                </div>
              ))
            ) : (
              <div className="text-center text-muted p-5 bg-light rounded-3 border">
                <MdVerifiedUser size={32} className="opacity-50 mb-2" />
                <p className="mb-0">No guarantors found for this customer.</p>
              </div>
            )}
          </div>

          <div className="detail-actions-card mt-4">
            <h6 className="card-title mb-4">Primary Verification Documents</h6>
            <div className="row">
              {renderDocumentPreview("Passport", data.passport, "Customer passport has not been uploaded")}
              {renderDocumentPreview("Driving License", data.drivingLicense, "Driving license attachment has not been uploaded")}
              {renderDocumentPreview("TIN Certificate", data.tin, "TIN certificate has not been uploaded")}
            </div>
          </div>

        </div>
      </div>
    );
  }

  return <div className="container mSupportLoading">{getInlineLoader()}</div>;
}